<template>
    <div class='container-fluid'>
    <div class="row">
        <div class="col-md-8">
            <form>
                <div class="form-group" :class="{'has-error': form.errors.has('name')}">
                    <label class="control-label margin-bottom-10" v-text="$lang.get('trans.name')"></label>
                    <input type="text" class="form-control" v-model="form.name" placeholder="...">
                    <span class="help-block" v-if="form.errors.has('name')" v-text="form.errors.get('name')"></span>
                </div>

                <div class="form-group" :class="{'has-error': form.errors.has('header')}">
                    <label class="control-label margin-bottom-10" v-text="$lang.get('trans.header')"></label>
                    <div class="pull-right section-actions">
                        <span class="propagate-action" :title="propagateBlockedReason('header')">
                            <button type="button" class="btn btn-sm btn-outline dark"
                                    :disabled="!canPropagate('header')"
                                    @click.prevent="openPropagate('header')"
                                    v-text="$lang.get('trans.propagate_header')"></button>
                        </span>
                        <toggle-button v-model="form.has_header" color="#000000" :labels="{checked: $lang.get('trans.with_header'), unchecked: $lang.get('trans.no_header')}" :width="97" :height="28" :sync="true"/>
                    </div>
                    <template-section v-model="form.header" type="header" v-if="form.has_header"> </template-section>
                    <hr v-else>
                    <span class="help-block" v-if="form.errors.has('header')" v-text="form.errors.get('header')"></span>
                </div>

                <div class="form-group" :class="{'has-error': form.errors.has('body')}">
                    <label class="control-label margin-bottom-10" v-text="$lang.get('trans.body')"></label>
                    <template-section v-model="form.body" type="body"> </template-section>
                    <span class="help-block" v-if="form.errors.has('body')" v-text="form.errors.get('body')"></span>
                </div>

                <div class="form-group" :class="{'has-error': form.errors.has('footer')}">
                    <label class="control-label margin-bottom-10" v-text="$lang.get('trans.footer')"></label>
                    <div class="pull-right section-actions">
                        <span class="propagate-action" :title="propagateBlockedReason('footer')">
                            <button type="button" class="btn btn-sm btn-outline dark"
                                    :disabled="!canPropagate('footer')"
                                    @click.prevent="openPropagate('footer')"
                                    v-text="$lang.get('trans.propagate_footer')"></button>
                        </span>
                        <toggle-button v-model="form.has_footer" color="#000000" :labels="{checked: $lang.get('trans.with_footer'), unchecked: $lang.get('trans.no_footer')}" :width="93" :height="28" :sync="true"/>
                    </div>
                    <template-section v-model="form.footer" type="footer" v-if="form.has_footer"> </template-section>
                    <hr v-else>
                    <span class="help-block" v-if="form.errors.has('footer')" v-text="form.errors.get('footer')"></span>
                </div>

                <div class="form-group text-right">
                    <button class="btn btn-outine dark" @click.prevent="save()">
                        <i class="glyphicon glyphicon-save"></i> Save
                    </button>
                </div>
            </form>
        </div>
        <div class="col-md-4" id='tm_right_section'>
            <template-options :variables="variables" :options="form.options"
                           @insert-variable="insertVariable"
                           @insert-function="insertFunction"
                           @options-changed="optionsChanged"
            > </template-options>
        </div>
    </div>

    <propagate-section-modal :section="propagate.section"
                             :targets="propagationTargets"
                             :busy="propagate.busy"
                             @confirm="runPropagate"
    > </propagate-section-modal>
    </div>
</template>

<script>
    import Form from 'services/form/Form'
    import EditorUtils from 'services/document-templates/tinymce/EditorUtils'
    import TemplateSection from './TemplateSection'
    import TemplateOptions from './TemplateOptions'
    import PropagateSectionModal from './PropagateSectionModal'


    export default {
        name: "template-manager",

        components: {
            TemplateSection,
            TemplateOptions,
            PropagateSectionModal,
        },

        data() {
            return {
                form: new Form({
                    name: null,
                    header: null,
                    body: null,
                    footer: null,
                    has_header: false,
                    has_footer: false,
                    usedVariables: [],
                    options: {}
                }),
                template: null,
                variables: [],
                propagationTargets: {},
                // the last persisted values of the two sections. Propagation
                // always sends what is in the database, so the buttons compare
                // the editor against this snapshot and stay disabled until the
                // template is saved again.
                savedSections: {
                    has_header: false,
                    header: null,
                    has_footer: false,
                    footer: null
                },
                propagate: {
                    // the header and the footer are propagated separately;
                    // this only holds the section the modal is opened for
                    section: 'header',
                    busy: false
                },
            }
        },

        computed: {
            usedVariables() {
                return this.filterUsedVariables(this.variables)
            }
        },

        methods: {

            filterUsedVariables(variables, parentKey, usedVariables = {}) {
                _.each(variables, (name, key) => {
                    key = typeof parentKey === 'undefined' ? key : `${parentKey}|${key}`

                    if (typeof name === 'string') {
                        if (this.variableIsUsed(key)) {
                            usedVariables[key] = name
                        }
                        return;
                    }

                    return this.filterUsedVariables(name, key, usedVariables)
                })

                return usedVariables
            },

            variableIsUsed(variable) {
                return _.includes(this.form.body, `{{$${variable}}}`)
                    || _.includes(this.form.header, `{{$${variable}}}`)
                    || _.includes(this.form.footer, `{{$${variable}}}`)
            },

            insertVariable({variable}) {
                EditorUtils.insertVariable(variable)
            },

            insertFunction({func}) {
                EditorUtils.insertFunction(func)
            },

            optionsChanged({options}) {
                this.form.options = options

                if (this.template) {
                    this.save()
                }
            },

            sectionIsEmpty(content) {
                if (!content) {
                    return true
                }

                let value = String(content)

                // a header made only of a logo / a layout table is not empty
                if (/<(img|table|hr|svg)\b/i.test(value)) {
                    return false
                }

                return value.replace(/<[^>]*>/g, '')
                    .replace(/&nbsp;/gi, ' ')
                    .replace(/\u00a0/g, ' ')
                    .trim() === ''
            },

            captureSavedSections() {
                this.savedSections = {
                    has_header: !!this.form.has_header,
                    header: this.form.header,
                    has_footer: !!this.form.has_footer,
                    footer: this.form.footer
                }
            },

            /**
             * The stored value is a full html document (the controller wraps it
             * on save) while tinyMCE hands back only the body, so both sides get
             * unwrapped before they are compared.
             */
            sectionContent(value) {
                if (!value) {
                    return ''
                }

                let match = String(value).match(/<body[^>]*>([\s\S]*)<\/body>/i)

                return (match ? match[1] : String(value)).trim()
            },

            sectionIsDirty(section) {
                return !!this.form[`has_${section}`] !== this.savedSections[`has_${section}`]
                    || this.sectionContent(this.form[section]) !== this.sectionContent(this.savedSections[section])
            },

            /**
             * Empty when the section can be propagated, otherwise the reason -
             * shown as a tooltip on the button.
             */
            propagateBlockedReason(section) {
                if (!this.template || this.sectionIsDirty(section)) {
                    return this.$lang.get(`trans.propagate_${section}_needs_save`)
                }

                // checked against the saved snapshot, not the editor: what gets
                // propagated is the content stored in the database
                if (!this.savedSections[`has_${section}`] || this.sectionIsEmpty(this.savedSections[section])) {
                    return this.$lang.get(`trans.propagate_${section}_missing`)
                }

                return ''
            },

            canPropagate(section) {
                return this.propagateBlockedReason(section) === ''
            },

            openPropagate(section) {
                if (!this.canPropagate(section)) {
                    return
                }

                this.propagate.section = section
                this.$modal.show('propagate-section-modal')
            },

            async runPropagate({scope, overwrite}) {
                this.propagate.busy = true

                try {
                    if (!Sincron.urls.propagateSection) {
                        this.$toaster.error('Error!')

                        return
                    }

                    let {data} = await axios.post(Sincron.urls.propagateSection, {
                        section: this.propagate.section,
                        scope: scope,
                        overwrite: overwrite
                    })

                    this.propagationTargets = data.targets
                    this.$toaster.success(data.message)
                    this.$modal.hide('propagate-section-modal')
                } catch (error) {
                    let message = error && error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : 'Error!'

                    this.$toaster.error(message)
                } finally {
                    this.propagate.busy = false
                }
            },

            async save() {
                this.form.usedVariables = this.usedVariables
                try {
                    let response = await this.form.post(Sincron.urls.saveTemplate)
                    this.$toaster.success(response.message)

                    if (!this.template) {
                        this.template = response.template
                        Sincron.urls.saveTemplate = response.updateTemplateUrl
                        Sincron.urls.propagateSection = response.propagateSectionUrl
                    }

                    // the editor and the database are in sync again, so the
                    // propagation buttons can be used
                    this.captureSavedSections()

                    return true
                } catch (error) {
                    this.$toaster.error(error && error.message ? error.message : 'Error!')

                    return false
                }
            }
        },

        mounted() {
            this.variables = Sincron.variables

            this.template = typeof Sincron.template !== 'undefined' ? Sincron.template : null

            this.propagationTargets = typeof Sincron.propagationTargets !== 'undefined' ? Sincron.propagationTargets : {}

            if (this.template) {
                this.form.name = this.template.name
                this.form.body = this.template.body
                this.form.has_header = this.template.has_header
                this.form.header = this.template.header
                this.form.has_footer = this.template.has_footer
                this.form.footer = this.template.footer
                this.form.options = this.template.options ? this.template.options : {}
            }

            this.captureSavedSections()

            this.filterUsedVariables(this.variables)
        }

    }
</script>

<style scoped>
    .section-actions {
        display: flex;
        align-items: center;
    }

    /* the tooltip lives on the wrapper, not on the button: a disabled button is
       not a reliable hover target across browsers */
    .section-actions > .propagate-action {
        display: inline-flex;
        margin: 0 10px 0 0;
    }

    .section-actions .btn {
        margin: 0;
    }

    /* bootstrap gives every label a 5px bottom margin, and the toggle's root
       element is a <label> - without this the switch sits above the centre
       line of the flex row and the button looks misaligned next to it */
    .section-actions .vue-js-switch {
        margin: 0;
    }
</style>
